/* eslint @typescript-eslint/no-explicit-any: "off" */
import React, { useCallback, useEffect, useState } from "react";
import { I18n } from "@aws-amplify/core";
import { Auth } from "aws-amplify";
import { Box, Button, TextField, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
// qrcode.react is not a direct dependency -- it arrives with aws-amplify-react,
// which uses it for the stock TOTPSetup widget this component replaces. It is left
// undeclared on purpose: adding it to package.json without regenerating
// package-lock.json would break `npm ci`. The coupling is safe because the two are
// removed together -- dropping aws-amplify-react means rewriting these components
// for Amplify v6 anyway. There are no published types, hence qrcode.react.d.ts.
import QRCode from "qrcode.react";

/**
 * Shared TOTP enrolment UI, used from two places:
 *
 *  - CustomTOTPSetup, during the MFA_SETUP challenge at sign-in (mandatory path)
 *  - the Security page, for a signed-in user enrolling voluntarily (self-service path)
 *
 * The Cognito calls are identical in both cases; only the surrounding chrome and
 * the user object differ, so the logic lives here rather than being duplicated.
 */

// Shown as the account name in the authenticator app. Without this the library
// default is the literal string "AWSCognito", which is meaningless to a user with
// several entries in their app.
const ISSUER = "CQA Landing Cards";

export interface TotpEnrolmentProps {
  /** Either a challenge user (MFA_SETUP) or a signed-in CognitoUser. */
  user: any;
  /** Called once the token is verified and TOTP is set as the preferred factor. */
  onComplete: (user: any) => void;
  submitLabel?: string;
}

export const TotpEnrolment: React.FunctionComponent<TotpEnrolmentProps> = ({
  user,
  onComplete,
  submitLabel,
}) => {
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    Auth.setupTOTP(user)
      .then((value: string) => {
        if (!cancelled) setSecret(value);
      })
      .catch(() =>
        setError(
          I18n.get(
            "Could not start two-factor setup. Please sign out and try again, or contact an administrator."
          )
        )
      );
    return () => {
      cancelled = true;
    };
  }, [user]);

  const verify = useCallback(() => {
    setError("");
    setBusy(true);
    Auth.verifyTotpToken(user, code.trim())
      .then(() => Auth.setPreferredMFA(user, "TOTP"))
      .then(() => onComplete(user))
      .catch(() => {
        setBusy(false);
        setError(
          I18n.get(
            "That code was not accepted. Codes change every 30 seconds — wait for a new one and try again."
          )
        );
      });
  }, [user, code, onComplete]);

  // otpauth://totp/<issuer>:<username>?secret=<secret>&issuer=<issuer>
  const otpauth = secret
    ? `otpauth://totp/${encodeURI(ISSUER)}:${encodeURIComponent(
        user.username
      )}?secret=${secret}&issuer=${encodeURI(ISSUER)}`
    : null;

  return (
    <>
      <Typography>
        {I18n.get(
          "Scan this code with an authenticator app — Microsoft Authenticator, Google Authenticator, 1Password or similar — then enter the six-digit code it shows."
        )}
      </Typography>

      {otpauth ? (
        <Box my={3}>
          <QRCode value={otpauth} size={180} />
          <Typography variant="caption" component="p">
            {I18n.get("Can't scan? Enter this key manually:")}
          </Typography>
          <Typography
            variant="caption"
            component="p"
            style={{ fontFamily: "monospace", wordBreak: "break-all" }}
          >
            {secret}
          </Typography>
        </Box>
      ) : (
        !error && (
          <Box my={3}>
            <Typography>{I18n.get("Preparing your security code…")}</Typography>
          </Box>
        )
      )}

      <TextField
        required
        autoFocus
        id="totpCode"
        key="totpCode"
        name="totpCode"
        label={I18n.get("Six-digit code")}
        autoComplete="off"
        inputProps={{ inputMode: "numeric", maxLength: 6 }}
        disabled={!secret || busy}
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />

      <Button
        variant="contained"
        color="primary"
        style={{ marginTop: 24 }}
        disabled={!secret || busy || code.trim().length < 6}
        onClick={verify}
        type="submit"
      >
        {submitLabel || I18n.get("Verify and continue")}
      </Button>

      {error && (
        <Box mt={2}>
          <Alert variant="outlined" severity="error">
            {error}
          </Alert>
        </Box>
      )}
    </>
  );
};

export default TotpEnrolment;
